import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "../../widgets/modal";
import TextField from "../../widgets/textField/textField";
import { BENCHMARK_SINGLECONTACT, DATE_FORMAT_UPDATED } from "../../constants/app.constants";
import {
  updateSingleApprovalScheduled,
  fetchApprovalScheduled,
} from "../scheduling/scheduling.utils";
import { useQuery } from "@tanstack/react-query";
import { FC } from "react";
import Swal from "sweetalert2";
import Loader from "../../widgets/Loader";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { dateFormat } from "../../constants/app.constants";

type OverviewModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  approveId: number;
};

type UpdatedValue = string | Date;

const SchedulingApprovalModal: FC<OverviewModalProps> = ({
  approveId,
  isOpen,
  onClose,
  onSubmit
}) => {
  const [estimatedYardage, setEstimatedYardage] = useState("");
  const [notExceedYardage, setNotExceedYardage] = useState("");
  const [overageApprovedBy, setOverageApprovedBy] = useState("");
  const [dateOverageApproved, setDateOverageApproved] = useState<Dayjs | null>(
    null
  );
  const [validationMsg, setValidationMsg] = useState("");
  const [isShowLoader, setIsShowLoader] = useState(false);

  const { isFetching, data: dataBenchmarkSingleApproval } = useQuery({
    queryKey: [BENCHMARK_SINGLECONTACT, approveId],
    queryFn: () => fetchApprovalScheduled(approveId),
    enabled: isOpen,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (dataBenchmarkSingleApproval) {
      setEstimatedYardage(dataBenchmarkSingleApproval.EstimatedYardage);
      setNotExceedYardage(dataBenchmarkSingleApproval.NotExceedYardage);
      setOverageApprovedBy(dataBenchmarkSingleApproval.OverageApprovedBy);
      setDateOverageApproved(
        dataBenchmarkSingleApproval.DateOverageApproved
          ? dayjs(dataBenchmarkSingleApproval.DateOverageApproved)
          : null
      );
      setIsShowLoader(false);
    }
  }, [dataBenchmarkSingleApproval, isOpen]);

    const onChangeFieldValues = useCallback(
    (id: string, updatedValue: UpdatedValue) => {
      if (id === "estimatedYardage") {
        setEstimatedYardage(updatedValue as string);
      } else if (id === "notExceedYardage") {
        setNotExceedYardage(updatedValue as string);
      } else if (id === "overageApprovedBy") {
        setOverageApprovedBy(updatedValue as string);
      }
    },
    []
  );

  const handleSubmit = async () => {
    if (!overageApprovedBy.trim()) {
      setValidationMsg("Please enter the approver’s name.");
      return;
    }
    if (!dateOverageApproved) {
      setValidationMsg("Please select the approval date.");
      return;
    }

    setValidationMsg("");
    setIsShowLoader(true);

    const success = await updateSingleApprovalScheduled(
      approveId,
      estimatedYardage,
      notExceedYardage,
      overageApprovedBy,
      dateOverageApproved.toDate()
    );

    setIsShowLoader(false);
    if (success) {
      Swal.fire({
        customClass: { popup: "sweetAlert" },
        icon: "success",
        showConfirmButton: false,
        timer: 1500,
        title: "Approved Updated Successfully!",
      });
      // onClose();
      onSubmit();
    } else {
      Swal.fire({
        icon: "warning",
        title: "Failed to Update Approval",
      });
    }
  };

  const handleCancel = () => {
    setEstimatedYardage("");
    setNotExceedYardage("");
    setOverageApprovedBy("");
    setDateOverageApproved(null);
    setValidationMsg("");
    setIsShowLoader(false);
    onClose();
  };

  return (
    <Modal show={isOpen} label="Overage Approval" onClose={handleCancel}>
      {validationMsg && (
        <div className="text-danger mb-2">{validationMsg}</div>
      )}
      <div className="requestModalSection">
        <div className="modalForm">
          {(isShowLoader || isFetching) && <Loader />}
          <form>
            <div className="modal-body">
              <div className="form-group row mb-3">
                <label className="col-md-4 col-form-label">
                  Estimated Yardage:
                </label>
                <div className="col-md-4">{estimatedYardage}</div>
              </div>

              <div className="form-group row mb-3">
                <label className="col-md-4 col-form-label">
                  Not to Exceed Yardage:
                </label>
                <div className="col-md-4">{notExceedYardage}</div>
              </div>

              <div className="form-group row mb-3">
                <label className="col-md-4 col-form-label">
                  Overage Approved By:
                  <span className="text-danger">*</span>
                </label>
                <div className="col-md-4">
                  <TextField
                    id="overageApprovedBy"
                    onChange={onChangeFieldValues}
                    type="text"
                    defaultValue={overageApprovedBy}
                  />
                </div>
              </div>

              <div className="form-group row mb-3">
                <label className="col-md-4 col-form-label">
                  Date Overage Approved:
                  <span className="text-danger">*</span>
                </label>
                <div className="col-md-6">
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Select date"
                      value={dateOverageApproved}
                      onChange={(newVal) => setDateOverageApproved(newVal)}
                      format={DATE_FORMAT_UPDATED}
                      disableFuture
                      slotProps={{
                        textField: {
                          size: "small",
                          className: "date-input-field"
                        },
                      }}
                    />
                  </LocalizationProvider>
                </div>
              </div>

              <div className="form-group row">
                <div className="col-md-4" />
                <div className="col-md-8 actionButtons">
                  <button
                    type="button"
                    className="btn cancelBtn"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-success submitBtn"
                    onClick={handleSubmit}
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export default SchedulingApprovalModal;
